// เขียนสถานะร้านแบบสดลง Firestore stores/{storeId} ให้ทีม support เปิดดูใน console ได้ทันที
//
// ใครเรียก:
//   plugins/storeStatus.plugin.ts → scheduleStoreStatusWrite() ทุกครั้งที่ jobchain/โต๊ะ/ใบเสร็จเปลี่ยน (main cashier)
//   posStore.openShop()           → markStoreStatusOpened(job)
//   หน้าปิดร้าน (ขั้นล้าง jobchain) → markStoreStatusClosed()
//
// กติกาสำคัญ:
//   - doc นี้มี field ของคนอื่นอยู่ด้วย (taxInfo, reload, ...) ห้ามเขียนทับทั้ง doc
//     ใช้ setDoc(..., { mergeFields }) แทนที่เฉพาะ field ที่ระบุ — live.deviceIds ถูกแทนทั้งก้อน
//     key ของเครื่องที่หายไปแล้วจึงไม่ค้าง (ดูเหตุผลที่ utils/storeStatus.ts storeStatusMergeFields)
//   - ห้าม throw เข้า flow ของ UI ทุกตัวกลืน error แล้ว console.warn
//   - ห้าม await ค้าง: Firestore resolve ต่อเมื่อ server ตอบ ถ้า offline promise จะค้างไปเรื่อย ๆ

import _ from "lodash";
import dayjs from "dayjs";
import { setDoc, deleteField } from "firebase/firestore";
import { RunMode } from "~~/interface/Pos";
import packageData from "~/package.json";
import { readComputerIdentity } from "~/modules/core/composables/useComputerIdentity";
import {
  buildStoreStatusDoc,
  emptyStoreStatusLive,
  storeStatusMergeFields,
} from "~~/modules/pos/utils/storeStatus";
import { resetLiveSummary } from "./useTableSync";

const WRITE_DEBOUNCE_MS = 2000;
// ร้านที่มี job เข้าตลอด debounce อย่างเดียวจะเลื่อนไปเรื่อย ๆ ไม่ได้เขียนสักที
const WRITE_MAX_WAIT_MS = 10_000;
const CLOSE_WAIT_MS = 3000;

// payload ล่าสุดที่เขียนสำเร็จ ตรงกันทุกตัวอักษร = ไม่ต้องเขียนซ้ำ
let lastPayloadJson = "";
// ปิดร้านแล้ว ห้ามเขียน opened/ตัวนับกลับไปทับจนกว่าจะ reload (หน้าปิดร้าน reload เอง)
let closed = false;

function writerInfo() {
  const computerId = useAuthStore().computerUuid || null;
  return {
    writerComputerId: computerId ? String(computerId) : null,
    writerComputerName: readComputerIdentity()?.name || null,
  };
}

function currentStoreId(): string {
  try {
    return useStoreId() || "";
  } catch {
    return "";
  }
}

function writeMerge(storeId: string, data: Record<string, any>) {
  return setDoc(useFirebaseFirestore().storeDocRef(storeId), data, {
    mergeFields: storeStatusMergeFields(data),
  });
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | void> {
  let timer: any;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/** เขียน live ของรอบนี้ตอนนี้เลย (main cashier เท่านั้น) — ไม่ throw */
export async function writeStoreStatusNow() {
  try {
    if (closed) return;
    if (useRunningMode().getMode() !== RunMode.MAIN_CASHIER) return;
    const storeId = currentStoreId();
    if (!storeId) return;

    const jobsStore = useJobsStore();
    const jobs = jobsStore.$state.jobs || [];
    // ยังไม่ได้ jobchain (เพิ่ง boot / เพิ่งล้างตอนปิดร้าน) ห้ามเอาศูนย์ไปทับยอดจริง
    if (jobs.length === 0) return;

    const doc = buildStoreStatusDoc({
      name: useShopStore().name,
      version: packageData.version,
      jobs,
      tables: jobsStore.$state.tables,
      tablesV2: jobsStore.$state.tables_v2,
      tableState: usePosStore().tableState,
      receipts: useReceiptsStore().receipts,
      openShop: jobsStore.openShop,
      closeJob: jobsStore.getCloseJob,
      crossDay: jobsStore.isOpenShopCrossDate,
      ...writerInfo(),
    });

    const json = JSON.stringify({ storeId, doc });
    if (json === lastPayloadJson) return;

    const data: Record<string, any> = { ...doc };
    // เปิดอยู่ = ไม่ควรมีเวลาปิดค้าง (เช่น เปิดรอบใหม่จากเครื่องรุ่นเก่าที่ไม่มี hook เปิดร้าน)
    if (doc.status === "opened") data.closed = deleteField();

    await writeMerge(storeId, data);
    lastPayloadJson = json;
  } catch (error) {
    console.warn("[storeStatus] เขียนสถานะร้านไม่สำเร็จ", error);
  }
}

const debouncedWrite = _.debounce(
  () => {
    writeStoreStatusNow();
  },
  WRITE_DEBOUNCE_MS,
  { maxWait: WRITE_MAX_WAIT_MS }
);

/** เรียกได้ถี่ ๆ — รวบเป็นการเขียนครั้งเดียวหลังข้อมูลนิ่ง 2 วิ */
export function scheduleStoreStatusWrite() {
  if (closed) return;
  debouncedWrite();
}

/** เปิดร้าน (ทุกโหมดที่กดเปิดร้าน) — ยิงแล้วลืม ไม่ throw */
export function markStoreStatusOpened(openJob: any) {
  try {
    const storeId = currentStoreId();
    if (!storeId) return;
    closed = false;
    lastPayloadJson = "";

    const opened = dayjs(openJob?.timestamp || undefined).toISOString();
    const data: Record<string, any> = {
      version: packageData.version,
      status: "opened",
      opened,
      closed: deleteField(),
      crossDay: false,
      live: emptyStoreStatusLive(openJob?.businessDate || null, writerInfo(), opened),
    };
    const name = (useShopStore().name || "").trim();
    if (name) data.name = name;

    writeMerge(storeId, data).catch((error) =>
      console.warn("[storeStatus] เขียนสถานะเปิดร้านไม่สำเร็จ", error)
    );
  } catch (error) {
    console.warn("[storeStatus] เขียนสถานะเปิดร้านไม่สำเร็จ", error);
  }
}

/**
 * ปิดร้าน (Main cashier หลังล้าง jobchain แล้ว)
 * - stores/{storeId}: status closed, ตัวนับของรอบเป็นศูนย์, ล้าง deviceIds
 *   bills/voided/paid คงยอดสุดท้ายของรอบไว้ให้ support ดูยอดวันที่ปิดไปได้ (รอบใหม่ hook เปิดร้านรีเซ็ตเอง)
 * - live เดิมของมือถือ: ลบ RTDB live + รีเซ็ต Firestore sync_data/live
 * รอไม่เกิน 3 วิ แล้วไปต่อเสมอ ไม่ throw
 */
export async function markStoreStatusClosed() {
  closed = true;
  debouncedWrite.cancel();

  const tasks: Promise<any>[] = [];
  try {
    const storeId = currentStoreId();
    if (storeId) {
      const jobsStore = useJobsStore();
      const now = dayjs().toISOString();
      const live = emptyStoreStatusLive(jobsStore.openShop?.businessDate || null, writerInfo(), now);
      // ยอดบิลของรอบที่เพิ่งปิด ไม่ใช่ตัวนับของรอบ — ไม่เขียนทับ
      const { bills, voided, paid, ...counters } = live;

      tasks.push(
        writeMerge(storeId, {
          version: packageData.version,
          status: "closed",
          closed: now,
          opened: deleteField(),
          crossDay: false,
          live: counters,
        }).catch((error) => console.warn("[storeStatus] เขียนสถานะปิดร้านไม่สำเร็จ", error))
      );
    }
  } catch (error) {
    console.warn("[storeStatus] เขียนสถานะปิดร้านไม่สำเร็จ", error);
  }

  try {
    tasks.push(
      resetLiveSummary().catch((error) => console.warn("[storeStatus] ล้าง live ไม่สำเร็จ", error))
    );
  } catch (error) {
    console.warn("[storeStatus] ล้าง live ไม่สำเร็จ", error);
  }

  await withTimeout(Promise.all(tasks), CLOSE_WAIT_MS);
}
